// src/pages/PacienteDetailPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Fade,
  Avatar,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmailIcon from '@mui/icons-material/Email';
import CakeIcon from '@mui/icons-material/Cake';
import BadgeIcon from '@mui/icons-material/Badge';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import NotesIcon from '@mui/icons-material/Notes';
import EditIcon from '@mui/icons-material/Edit';

const PacienteDetailPage = () => {
  const { id } = useParams();
  const [paciente, setPaciente] = useState(null);
  const [consultas, setConsultas] = useState([]);
  const [consultaReciente, setConsultaReciente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [proximaCita, setProximaCita] = useState(null);

  const fetchData = async (signal) => {
    setLoading(true);
    try {
      const reqPaciente = axiosInstance.get(`/pacientes/${id}/`, { signal });
      const reqConsultas = axiosInstance.get(`/consultas/`, { params: { paciente: id }, signal });
      const reqCitas = axiosInstance.get(`/citas/`, { params: { paciente: id }, signal });

      const [responsePaciente, responseConsultas, responseCitas] = await Promise.all([
        reqPaciente,
        reqConsultas,
        reqCitas
      ]);

      // 1) Paciente
      const pacienteData = responsePaciente.data;
      setPaciente(pacienteData);

      // 2) Consultas
      const consultasData = Array.isArray(responseConsultas.data)
        ? responseConsultas.data
        : (responseConsultas.data?.results || []);

      const consultasFiltradas = consultasData.filter((consulta) => {
        const pacienteId = String(id);
        const porIdDirecto = String(consulta.paciente_id ?? consulta.paciente) === pacienteId;
        const porObjeto = typeof consulta.paciente === 'object'
          && String(consulta.paciente?.id_paciente) === pacienteId;
        return porIdDirecto || porObjeto;
      });

      setConsultas(consultasFiltradas);

      if (consultasFiltradas.length > 0) {
        const consultasOrdenadas = [...consultasFiltradas].sort(
          (a, b) => new Date(b.fecha_hora_consulta) - new Date(a.fecha_hora_consulta)
        );
        setConsultaReciente(consultasOrdenadas[0]);
      } else {
        setConsultaReciente(null);
      }

      // 3) Citas
      const citasData = Array.isArray(responseCitas.data)
        ? responseCitas.data
        : (responseCitas.data?.results || []);

      let citasPaciente = citasData;
      if (citasData.length > 0) {
        const nombreCompletoPaciente = `${pacienteData.nombres} ${pacienteData.apellidos}`;
        citasPaciente = citasData.filter((cita) => {
          if (typeof cita.paciente === 'string') {
            return cita.paciente === nombreCompletoPaciente;
          }
          const pacienteId = String(id);
          const porIdDirecto = String(cita.paciente_id ?? cita.paciente) === pacienteId;
          const porObjeto = typeof cita.paciente === 'object'
            && String(cita.paciente?.id_paciente) === pacienteId;
          return porIdDirecto || porObjeto;
        });
      }

      const ahora = new Date();
      const citasFuturas = citasPaciente.filter((cita) =>
        ['Programada', 'Confirmada'].includes(cita.estado_cita) &&
        new Date(cita.fecha_hora_cita) > ahora
      );

      if (citasFuturas.length > 0) {
        const citasOrdenadas = citasFuturas.sort(
          (a, b) => new Date(a.fecha_hora_cita) - new Date(b.fecha_hora_cita)
        );
        setProximaCita(citasOrdenadas[0]);
      } else {
        setProximaCita(null);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error al cargar los datos:', err);
      toast.error('No se pudieron cargar los datos del paciente.');
    } finally {
      setLoading(false);
      setTimeout(() => setFadeIn(true), 100);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    fetchData(ctrl.signal);
    return () => ctrl.abort();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Construye líneas de tiempo de antecedentes a partir de TODAS las consultas (ascendente)
  const buildAntecedenteTimeline = (field) => {
    const sorted = [...consultas].sort(
      (a, b) => new Date(a.fecha_hora_consulta) - new Date(b.fecha_hora_consulta)
    );
    const out = [];
    let lastText = null;
    for (const c of sorted) {
      const raw = (c?.[field] || '').trim();
      if (raw) {
        // Evitar duplicar exactamente el mismo texto consecutivo
        if (!lastText || raw !== lastText) {
          out.push({
            date: c.fecha_hora_consulta,
            text: raw
          });
          lastText = raw;
        }
      }
    }
    return out;
  };

  const antecedentes = useMemo(() => {
    return {
      patologicos: buildAntecedenteTimeline('antecedentes_patologicos'),
      alergicos: buildAntecedenteTimeline('antecedentes_alergicos'),
      toxicologicos: buildAntecedenteTimeline('antecedentes_toxicologicos'),
      quirurgicos: buildAntecedenteTimeline('antecedentes_quirurgicos'),
      // opcional: gineco-obstétricos para pacientes femeninas
      gineco: buildAntecedenteTimeline('antecedentes_gineco_obstetricos'),
    };
  }, [consultas]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh'
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando datos del paciente...
        </Typography>
      </Box>
    );
  }

  if (!paciente) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          No se pudo cargar la información del paciente
        </Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to="/doctor/pacientes"        
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
        >
          Volver a la lista de pacientes
        </Button>
      </Box>
    );
  }

  const avatarColor = paciente.sexo === 'Masculino' ? 'primary.main' : 'secondary.main';

  const getInitials = () => {
    return `${paciente.nombres.charAt(0)}${paciente.apellidos.charAt(0)}`;
  };

  const renderAntecedenteSection = (title, entries) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="medium" color="text.primary" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {entries.length > 0 ? (
          <Box>
            {entries.map((e, idx) => (
              <Box
                key={`${title}-${idx}`}
                sx={{
                  pl: 2,
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                  mb: 1.5
                }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={idx === 0 ? 'Registro inicial' : `Actualización ${formatDateShort(e.date)}`}
                  sx={{ mb: 1 }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-line'
                  }}
                >
                  {e.text}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              pl: 2,
              fontStyle: 'italic',
              color: 'text.secondary'
            }}
          >
            No se han registrado datos
          </Typography>
        )}
      </Box>
    );
  };

  const noAntecedentes =
    antecedentes.patologicos.length === 0 &&
    antecedentes.alergicos.length === 0 &&
    antecedentes.toxicologicos.length === 0 &&
    antecedentes.quirurgicos.length === 0 &&
    (paciente.sexo?.toLowerCase()?.startsWith('fem') ? antecedentes.gineco.length === 0 : true);

  return (
    <Box p={4} sx={{ maxWidth: '1600px', margin: '0 auto' }}>
      {/* Botón para volver */}
      <Button
        variant="outlined"
        component={RouterLink}
        to="/doctor/pacientes"            
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 4,
          borderRadius: '8px',
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateX(-5px)' }
        }}
      >
        Volver a Pacientes
      </Button>

      {/* Botón Tablas de Crecimiento OMS (solo pacientes pediátricos) */}
      {paciente?.age != null && paciente.age <= 19 && (
        <Button
          variant="outlined"
          color="primary"
          component={RouterLink}
          to={`/doctor/pacientes/${id}/crecimiento`}
          startIcon={<ChildCareIcon />}
          sx={{ mb: 4, ml: 2, borderRadius: '8px' }}
        >
          Tablas
        </Button>
      )}

      {/* Cabecera */}
      <Fade in={fadeIn} timeout={800}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ borderRadius: '12px', p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 4
            }}
          >
            <Avatar
              src={paciente.foto_perfil}
              sx={{
                width: 120,
                height: 120,
                bgcolor: avatarColor,
                fontSize: '2.5rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              }}
            >
              {getInitials()}
            </Avatar>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {paciente.nombres} {paciente.apellidos}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BadgeIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 22 }} />
                    <Typography variant="body1">
                      <Typography component="span" color="text.secondary" variant="body2">
                        DNI:
                      </Typography>{' '}
                      {paciente.dni || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CakeIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 22 }} />
                    <Typography variant="body1">
                      <Typography component="span" color="text.secondary" variant="body2">
                        Edad:
                      </Typography>{' '}
                      {paciente.age !== null ? `${paciente.age} años` : 'No disponible'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 22 }} />
                    <Typography variant="body1" noWrap sx={{ maxWidth: '100%' }}>
                      <Typography component="span" color="text.secondary" variant="body2">
                        Email:
                      </Typography>{' '}
                      {paciente.email || 'No disponible'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 22 }} />
                    <Typography variant="body1">
                      <Typography component="span" color="text.secondary" variant="body2">
                        Nacimiento:
                      </Typography>{' '}
                      {formatDate(paciente.fecha_nacimiento)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Próxima cita */}
          {proximaCita ? (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CalendarTodayIcon color="primary" />
              <Box>
                <Typography variant="subtitle2" color="primary.main">
                  Próxima cita
                </Typography>
                <Typography variant="body2">
                  {formatDate(proximaCita.fecha_hora_cita)} - {proximaCita.motivo_cita}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CalendarTodayIcon color="disabled" />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Sin citas programadas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Este paciente no tiene citas próximas
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Fade>

      {/* Pestañas */}
      <Fade in={fadeIn} timeout={1000}>
        <Box sx={{ width: '100%', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="patient information tabs"
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                fontSize: '1rem',
                textTransform: 'none',
                minWidth: 120,
                fontWeight: 'medium',
                py: 1.5
              }
            }}
          >
            <Tab label="Información Adicional" id="tab-0" />
            <Tab label="Historial de Consultas" id="tab-1" />
            <Tab
              label="Nueva Consulta"
              id="tab-2"
              component={RouterLink}
              to={`/doctor/pacientes/${id}/nueva-consulta`}
            />
          </Tabs>

          {/* Panel de Información Adicional */}
          <div role="tabpanel" hidden={tabValue !== 0}>
            {tabValue === 0 && (
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ p: 4, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              >
                <Grid container spacing={4}>
                  {/* Columna 1 */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="h6">Teléfono</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ ml: 4 }}>
                        {paciente.telefono_principal || 'No especificado'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <WorkIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="h6">Profesión</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ ml: 4 }}>
                        {paciente.profesion || 'No especificado'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <BloodtypeIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="h6">Grupo Sanguíneo</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ ml: 4 }}>
                        {paciente.grupo_sanguineo || 'No especificado'}
                      </Typography>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <NotesIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="h6">Observaciones</Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          ml: 4,
                          whiteSpace: 'pre-line',
                          fontStyle: paciente.observaciones ? 'normal' : 'italic',
                          color: paciente.observaciones ? 'text.primary' : 'text.secondary',
                        }}
                      >
                        {paciente.observaciones || 'Sin observaciones'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Columna 2: Antecedentes (acumulados) */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" color="primary" sx={{ mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      Antecedentes Médicos
                    </Typography>

                    {!noAntecedentes ? (
                      <>
                        {renderAntecedenteSection('Antecedentes Personales Patológicos', antecedentes.patologicos)}
                        {renderAntecedenteSection('Antecedentes Inmuno-alérgicos', antecedentes.alergicos)}
                        {renderAntecedenteSection('Antecedentes Toxicológicos', antecedentes.toxicologicos)}
                        {renderAntecedenteSection('Antecedentes Quirúrgicos', antecedentes.quirurgicos)}
                        {paciente.sexo?.toLowerCase()?.startsWith('fem') && renderAntecedenteSection('Antecedentes Gineco-Obstétricos', antecedentes.gineco)}
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: '8px' }}>
                        <Typography variant="body1" color="text.secondary">
                          No hay consultas registradas con información de antecedentes médicos.
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Button
                            variant="contained"
                            component={RouterLink}
                            to={`/doctor/pacientes/${id}/nueva-consulta`}   
                            startIcon={<AddCircleOutlineIcon />}
                            fullWidth
                            sx={{ maxWidth: 250 }}
                          >
                            Crear Primera Consulta
                          </Button>
                          <Button
                            variant="outlined"
                            component={RouterLink}
                            to={`/doctor/pacientes/${id}/edit`}
                            startIcon={<EditIcon />}
                            fullWidth
                            sx={{ maxWidth: 250 }}
                          >
                            Editar Paciente
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )}
          </div>

          {/* Panel Historial de Consultas */}
          <div role="tabpanel" hidden={tabValue !== 1}>
            {tabValue === 1 && (
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ p: 4, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Consultas Médicas</Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to={`/doctor/pacientes/${id}/nueva-consulta`}
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{
                      borderRadius: '8px',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      }
                    }}
                  >
                    Nueva Consulta
                  </Button>
                </Box>

                {consultas.length > 0 ? (
                  <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', overflow: 'hidden' }}>
                    {consultas.map((consulta, index) => (
                      <div key={consulta.id_consulta}>
                        <ListItem disablePadding>
                          <ListItemButton
                            component={RouterLink}
                            to={`/doctor/consultas/${consulta.id_consulta}`}
                            sx={{
                              py: 2,
                              px: 3,
                              transition: 'all 0.2s ease',
                              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)', transform: 'translateX(5px)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
                              <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48, fontSize: '1rem' }}>
                                <EventNoteIcon />
                              </Avatar>
                            </Box>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 0.5 }}>
                                  {formatDate(consulta.fecha_hora_consulta)}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {consulta.diagnostico_principal}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                        {index < consultas.length - 1 && <Divider component="li" />}
                      </div>
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 6,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '8px'
                    }}
                  >
                    <MedicalServicesIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Este paciente no tiene consultas registradas
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Crea la primera consulta usando el botón "Nueva Consulta"
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </div>
        </Box>
      </Fade>
    </Box>
  );
};

export default PacienteDetailPage;
